/**
 * Money utility functions
 */

/**
 * Format number as currency (USD by default)
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format number as currency without symbol
 */
export function formatCurrencyAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || isNaN(amount)) return '0.00';
  
  return amount.toFixed(2);
}

/**
 * Parse currency string to number
 */
export function parseCurrency(value: string | null | undefined): number {
  if (!value) return 0;
  
  // Remove currency symbols and commas
  const cleaned = value.replace(/[$,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Calculate percentage
 */
export function calculatePercentage(part: number, total: number): number {
  if (total === 0) return 0;
  return (part / total) * 100;
}

/**
 * Format percentage
 */
export function formatPercentage(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  return `${value.toFixed(decimals)}%`;
}

/**
 * Round to 2 decimal places (for money)
 */
export function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * Keep at most `maxFractionDigits` digits after the first decimal point.
 * Preserves in-progress input such as "" or "1.".
 */
export function limitDecimalPlaces(raw: string, maxFractionDigits = 2): string {
  if (!raw) {
    return raw;
  }
  const negative = raw.trim().startsWith('-');
  const digits = raw.replace(/[^\d.]/g, '');
  const dot = digits.indexOf('.');
  if (dot === -1) {
    return (negative ? '-' : '') + digits;
  }
  const intPart = digits.slice(0, dot);
  const fraction = digits.slice(dot + 1).replace(/\./g, '');
  const max = Number.isFinite(maxFractionDigits) && maxFractionDigits >= 0 ? maxFractionDigits : 2;
  return (negative ? '-' : '') + intPart + '.' + fraction.slice(0, max);
}

/**
 * Add two money amounts
 */
export function addMoney(amount1: number, amount2: number): number {
  return roundMoney(amount1 + amount2);
}

/**
 * Subtract two money amounts
 */
export function subtractMoney(amount1: number, amount2: number): number {
  return roundMoney(amount1 - amount2);
}

/**
 * Multiply money amount by factor
 */
export function multiplyMoney(amount: number, factor: number): number {
  return roundMoney(amount * factor);
}

/**
 * Calculate tax amount
 */
export function calculateTax(amount: number, taxRate: number): number {
  return roundMoney(amount * (taxRate / 100));
}

/**
 * Calculate total with tax
 */
export function calculateTotalWithTax(amount: number, taxRate: number): number {
  return addMoney(amount, calculateTax(amount, taxRate));
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  if (value >= 1000000000) {
    return (value / 1000000000).toFixed(1) + 'B';
  }
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  
  return value.toString();
}

